import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Add, Refresh } from '@mui/icons-material';
import { Button, IconButton, Tooltip } from '@mui/material';
import CopilotIcon from 'components/shared/CopilotIcon/CopilotIcon';
import ReusableActions from 'pages/OrgHistory/components/ReusableDataGrid/ReusableActions';
import { showToast } from 'utils/reusableToast';

import AdminReactivateUserModal from './AdminReactivateUserModal';
import ExpandHistory from './ExpandHistory';
import HistoryModal from './HistoryModal';
import ResetProfileButton from './ResetProfileButton';
import StatusBadge from './StatusBadge';
import UserSelectionModal from './UserSelectionModal';

import { useGetActiveOrganizationsQuery, useGetInProgressOrganizationsQuery } from '../../state/rtkQuery/org/orgQueries';
import { useGetPlatformsQuery } from '../../state/rtkQuery/platform/platformQueries';
import { useReactivateUserMutation, useUpdateUserMembershipBulkMutation } from "../../state/rtkQuery/user/userMutations";
import { useGetCostCenterUsersQuery, useLazyGetActiveUserStatusQuery, useLazyGetUserQuery } from '../../state/rtkQuery/user/userQueries';
import { useGetUserAccessQuery } from '../../state/rtkQuery/access/accessQueries';
import { useCurrentUserRoleNames, usePermission } from 'components/RequirePermission/useAccessPermissions';
import { isCrossOrgAdminRole } from 'components/RequirePermission/RequireEcrOpsRole';
import { PERMISSIONS } from 'utils/accessManagement/permissionKeys';
import '../../pages/ecrOps/EcrOpsLayout.css';
// Enhanced StatusBadge component

// Enhanced History component


// `profileExtra` is shown under the profile card; `copilotOrgIds` (organization ids) adds a Copilot column marking the memberships that carry a
// Copilot license with a Copilot icon. Both are optional, so other pages that use this component are unchanged.
const UserOrgSearch = ({ initialEmail = '', hideSearchBar = false, profileExtra = null, copilotOrgIds = null }) => {
  // A Copilot column is added only when the caller passes the list (even an empty one).
  const showCopilotColumn = Array.isArray(copilotOrgIds);
  // With extra profile content (the ECR Ops search page) the layout is wide: the profile, billing and extra cards sit in one
  // row and the memberships table below them uses the full width. Without it the layout is unchanged.
  const wideLayout = Boolean(profileExtra);
  const [toast, setToast] = useState({ open: false, message: '', type: 'info' });
  useEffect(() => {
    if (toast.open) {
      const timer = setTimeout(() => {
        setToast({ ...toast, open: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toast.open]);

  // Add this component at the top of your file
  const LoadingOverlay = ({ show }) => (
    show ? (
      <div style={{
        position: "absolute",
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 999,
        background: "rgba(255,255,255,0.6)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "opacity 0.2s",
      }}>
        <div>
          <svg width="48" height="48" viewBox="0 0 50 50">
            <circle cx="25" cy="25" r="20" fill="none" stroke="#0b756c" strokeWidth="5" strokeDasharray="31.4 31.4" strokeLinecap="round">
              <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
            </circle>
          </svg>
          <div style={{ color: "#0b756c", fontWeight: 600, marginTop: 12, textAlign: "center" }}>Loading...</div>
        </div>
      </div>
    ) : null
  );

  // This component only ever renders on /ecr-ops/search (see
  // EcrOpsUnifiedSearch/index.js), already gated by RequireEcrOpsRole to
  // EnterpriseAdmin/OperationsAdmin (global) or OperationsUser (org-scoped,
  // via isOperationsUserInAnyOrg). EnterpriseAdmin/OperationsAdmin keep flat
  // cross-org access here, same as before. OperationsUser is org-scoped
  // everywhere else in this app, so it's scoped here too, per row (org) of
  // the searched-for user's memberships — the acting admin's own org access
  // is fetched below and checked against each row's org id.
  const roleNames = useCurrentUserRoleNames();
  const hasUserUpdatePermission = usePermission(PERMISSIONS.USER_ID_UPDATE);

  const [actionLoading, setActionLoading] = useState(false);
  const [orgNameLoading, setOrgNameLoading] = useState(true);
  const isMounted = useRef(true);
  const [reactivateUser, { isLoading: isReactivating }] = useReactivateUserMutation();


  const [updateUserMembershipBulk, { isLoading: isUpdating }] = useUpdateUserMembershipBulkMutation();
  const { data: costCentersData, isSuccess: isCostCentersSuccess, loading: isCostCenterListDataLoading } = useGetCostCenterUsersQuery();
  const loggedInEmail = useSelector(state => state.auth?.userAccount?.username?.toLowerCase());
  const { data: myOrgAccess } = useGetUserAccessQuery(
    { email: loggedInEmail },
    { skip: !loggedInEmail }
  );
  // Gates the membership-status actions (remove from org / reset profile /
  // add to org) per row — reactivating an inactive account has no permission
  // gate of its own beyond the page-level ecr-ops role check.
  const canUpdateUserMembershipForOrg = (orgId) => {
    if (!hasUserUpdatePermission) return false;
    if (isCrossOrgAdminRole(roleNames)) return true;
    return (myOrgAccess?.organization ?? []).some(
      (org) => org.org_id === orgId && org.role === "OperationsUser"
    );
  };
  // Add a refresh state
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  // Add this function to refresh the data
  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1);
  };
  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);


  const [email, setEmail] = useState(initialEmail || '');
  const [emailError, setEmailError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [trigger, { data, isFetching, error, reset, loading: isUserDataLoading }] = useLazyGetUserQuery({
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const [showResults, setShowResults] = useState(false);
  // Update your queries to use the refresh trigger
  const { data: platformData, isSuccess: isPlatformSuccess } = useGetPlatformsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: false,
  }); const { data: allOrgs, isSuccess: isOrgsSuccess } = useGetActiveOrganizationsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: false,
  });
  const { data: inProgressOrgs, isSuccess: isInProgressOrgsSuccess } = useGetInProgressOrganizationsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: false,
  });


  const [triggerActiveUserStatus, { data: activeUserStatus, isLoading: isActiveUserLoading }] = useLazyGetActiveUserStatusQuery();

  // Auto-trigger when the shared search bar updates initialEmail
  const lastInitialEmail = useRef('');
  useEffect(() => {
    if (!initialEmail || !isPlatformSuccess || !platformData?._id) return;
    if (initialEmail === lastInitialEmail.current) return;
    lastInitialEmail.current = initialEmail;
    setEmail(initialEmail);
    reset();
    setSelectedUser(null);
    setShowResults(false);
    setShowUserSelection(false);
    setSearchResults([]);
    triggerActiveUserStatus({ email: initialEmail });
    trigger({ email: initialEmail, platformId: platformData._id }).then((result) => {
      if (!result?.data || !Array.isArray(result.data) || result.data.length === 0) {
        setToast({ open: true, message: result?.error?.error || 'No such user found.', type: 'error' });
      } else if (result.data.length === 1) {
        setSelectedUser(result.data[0]);
        setShowResults(true);
        setEmail(result.data[0].email);
      } else {
        setSearchResults(result.data);
        setShowUserSelection(true);
      }
    });
  }, [initialEmail, isPlatformSuccess, platformData?._id]); // eslint-disable-line


  // Email validation
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (e) => {

    const value = e.target.value;
    setEmail(value);
    if (value && !isValidEmail(value)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }
    if (value === '') {
      setShowResults(false);
      setSelectedUser(null);
      setShowUserSelection(false);
    }
  };

  function checkOrgAdminError(result, orgName) {
    if (
      result?.data?.message &&
      result.data.message.includes("ADResourceNotFound/Elevated/ServiceAccount")
    ) {
      showToast({
        title: "Org Admin Permission Required",
        description: `Operation failed! 
        You are not an org admin for the ${orgName} org. Please get yourself added as an org admin and try again.`,
        type: "error",
      });
      return true;
    }
    return false;
  }
  const handleSearch = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (emailError) return;
    // Add minimum length check
    if (!email || email.trim().length < 3) {
      setEmailError('Please enter at least 3 characters');
      return;
    }
    if (email) {
      // Reset cache and states before searching
      reset(); // This clears the cache
      setSelectedUser(null);
      setShowResults(false);
      setShowUserSelection(false);
      setSearchResults([]);

      triggerActiveUserStatus({ email });

      // Small delay to ensure reset completes
      await new Promise(resolve => setTimeout(resolve, 50));

      const result = await trigger({ email, platformId: platformData?._id });

      if (result?.error || !result?.data || !Array.isArray(result.data) || result.data.length === 0) {
        setToast({ open: true, message: result?.error?.error || 'No such user found.', type: 'error' });
        setEmail('');
        setShowResults(false);
        setSelectedUser(null);
        setShowUserSelection(false);
        setSearchResults([]);
      } else if (result.data.length === 1) {
        // Only one user found, show directly
        setSelectedUser(result.data[0]);
        setShowResults(true);
        setShowUserSelection(false);
        setEmail(result.data[0].email);
        setSearchResults([]);
      } else {
        // Multiple users found, show selection modal
        setSearchResults(result.data);
        setShowUserSelection(true);
        setShowResults(false);
        setSelectedUser(null);
      }
    }
  };

  const updateSelectedUser = async () => {
    if (selectedUser && email) {
      const result = await trigger({ email: selectedUser.email, platformId: platformData?._id });
      if (result?.data && result.data.length > 0) {
        // Find the exact user by _id to maintain selection
        const updatedUser = result.data.find(u => u._id === selectedUser._id);
        if (updatedUser) {
          setSelectedUser(updatedUser);
        }
      }
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setShowResults(true);
    setShowUserSelection(false);
    setEmail(user.email);
    setSearchResults([]); // Clear search results after selection
    // Optionally trigger active user status for the selected user
    triggerActiveUserStatus({ email: user.email });
  };

  const handleCloseUserSelection = () => {
    setShowUserSelection(false);
    setSelectedUser(null);
    setShowResults(false);
    setSearchResults([]); // Clear search results when closing
  };

  // Build orgId to orgName map. Active + In-Progress are the only two
  // org-level statuses this system has — a user's org membership can be
  // recorded against an org that's since moved to (or started in) either
  // status, so both lists are merged here or a membership row falls back to
  // showing the raw org ID whenever that org isn't currently Active.
  const orgIdToName = useMemo(() => {
    if (!isOrgsSuccess || !allOrgs || !isInProgressOrgsSuccess || !inProgressOrgs) {
      setOrgNameLoading(true);
      return {};
    }
    setOrgNameLoading(false);
    return [...allOrgs, ...inProgressOrgs].reduce((acc, org) => {
      acc[org._id] = org.name;
      return acc;
    }, {});
  }, [allOrgs, isOrgsSuccess, inProgressOrgs, isInProgressOrgsSuccess]);

  // Extract orgs from the nested structure
  let orgs = [];
  let user = selectedUser;
  if (
    selectedUser &&
    isPlatformSuccess &&
    platformData?._id &&
    selectedUser.platforms &&
    selectedUser.platforms[platformData._id] &&
    selectedUser.platforms[platformData._id].organizations
  ) {
    orgs = Object.entries(selectedUser.platforms[platformData._id].organizations).map(
      ([orgId, orgObj]) => ({
        id: orgId,
        ...orgObj,
      })
    );
    if (orgs.length > 0) {
      orgs.sort((a, b) => {
        if (a.membership_status === "Joined" && b.membership_status !== "Joined") return -1;
        if (a.membership_status !== "Joined" && b.membership_status === "Joined") return 1;
        return 0;
      });
    }
  }
  const orgStatusCounts = useMemo(() => {
    if (!orgs.length) return {};
    return orgs.reduce((counts, org) => {
      const status = org.membership_status;
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    }, {});
  }, [orgs]);
  let costCenterName = "";
  let ccId = "";
  if (
    user &&
    platformData?._id &&
    user.platforms &&
    user.platforms[platformData._id]
  ) {
    ccId = user.platforms[platformData._id].cost_center_id;
  }
  if (
    ccId &&
    isCostCentersSuccess &&
    Array.isArray(costCentersData?.cost_centers)
  ) {
    const ccObj = costCentersData.cost_centers.find(cc => cc._id === ccId);
    costCenterName = ccObj ? ccObj.name : ccId || "Not found";
  }
  const opData = user?.op_data || {};
  const hasOpProfile = Boolean(
    costCenterName ||
    opData.rc_leader_name ||
    opData.gps_indicator ||
    opData.crcc_business_name ||
    opData.crcc_business_area ||
    opData.crcc_business_line
  );

  const showOverlay = actionLoading || orgNameLoading || isCostCenterListDataLoading || isUserDataLoading || isFetching;

  return (
    <>
      <LoadingOverlay show={showOverlay} />
      <UserSelectionModal
        isLoading={isFetching}
        open={showUserSelection}
        onClose={handleCloseUserSelection}
        users={searchResults}
        onSelectUser={handleSelectUser}
      />
      <div>
        <div style={{
          maxWidth: 1300,
          margin: '0 auto',
        }}>

          {!hideSearchBar && <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12, marginBottom: '1.5rem' }}>          <input
            type="email"
            placeholder="Enter user email"
            value={email}
            onChange={handleEmailChange}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              border: '1px solid #d0d7de',
              fontSize: '1rem',
              background: '#fff',
              color: '#222',
              outline: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
              transition: 'border 0.2s',
            }}
          />
            <button
              type="submit"
              disabled={isFetching || !!emailError}
              style={{
                background: isFetching || emailError ? '#b2d8d8' : '#0b756c',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem 1.5rem',
                fontWeight: 600,
                fontSize: '1rem',
                cursor: isFetching || emailError ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 8px rgba(11, 117, 108, 0.2)',
                transition: 'background 0.2s',
              }}
            >{isFetching ? 'Searching...' : 'Search'}</button>
          </form>}

          {!hideSearchBar && emailError && (
            <div style={{
              marginBottom: '1rem',
              fontSize: '1rem',
              color: '#d32f2f',
              textAlign: 'center',
              fontWeight: 500,
            }}>
              {emailError}
            </div>
          )}

          {!hideSearchBar && toast.open && (
            <div style={{
              marginBottom: '1rem',
              fontSize: '1rem',
              color: '#d32f2f',
              textAlign: 'center',
              fontWeight: 500,
            }}>
              {toast.message}
            </div>
          )}

          {isFetching && <p style={{ color: '#0b756c', fontWeight: 500, textAlign: 'center' }}>Loading...</p>}

          {showResults && user && (
            <div style={{
              marginTop: 20,
              display: 'flex',
              flexDirection: wideLayout || window.innerWidth < 768 ? 'column' : 'row',
              gap: '20px',
              alignItems: 'stretch',
            }}>
              {/* LEFT COLUMN: Profile Card */}
              <div style={wideLayout ? {
                background: '#fff',
                borderRadius: '12px',
                padding: '1.25rem 1.5rem',
                boxShadow: '0 2px 8px rgba(11,117,108,0.06)',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
                gap: 16,
                alignItems: 'stretch',
              } : {
                flex: '0 0 340px',
                background: '#fff',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(11,117,108,0.06)',
              }}>
                <div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  // marginBottom: 16
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%",
                    background: "#e0f7fa", color: "#0b756c",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 700, fontSize: 24, letterSpacing: 1,
                    border: "2px solid #b2ebf2"
                  }}>
                    {user.first_name?.[0] || user.last_name?.[0] || "U"}
                  </div>

                  {/* Move status badge here */}
                  <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "#222", marginRight: 12 }}>
                      {user.full_name || `${user.last_name}, ${user.first_name}`}
                    </div>
                    <span style={{
                      fontWeight: 600,
                      color: activeUserStatus?.is_active ? "#22A06B" : "#d32f2f",
                      fontSize: 12,
                      background: activeUserStatus?.is_active ? "#e6f4ea" : "#ffeaea",
                      borderRadius: 6,
                      padding: "3px 10px",
                      marginRight: 8
                    }}>
                      {activeUserStatus?.is_active ? "ACTIVE" : "INACTIVE"}
                    </span>
                    {/* Show small icon button for reactivation if inactive */}
                    {!activeUserStatus?.is_active && (
                      <Tooltip title="Reactivate Account">
                        <span>
                        <IconButton
                          onClick={() => {
                            setReactivateTarget(user?.email);
                            setShowReactivateConfirm(true);
                          }}
                          disabled={isReactivating}
                          size="small"
                          sx={{
                            color: "#0b756c",
                            ml: 1,
                            opacity: isReactivating ? 0.7 : 1,
                          }}
                        >
                          <Refresh fontSize="small" />
                        </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {/* Email display */}
                <div style={{
                  fontSize: 14,
                  color: "#666",
                  marginBottom: 16,
                  paddingLeft: 72, // Align with name (56px avatar + 16px gap)
                  wordBreak: "break-word"
                }}>
                  {user.email}
                </div>
                <div style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  marginBottom: 16,
                }}>
                  {user.country && <span style={{
                    background: "#e0f7fa",
                    borderRadius: 6,
                    padding: "3px 10px",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}>
                    <span style={{ fontSize: "12px" }}>🌎</span> {user.country}
                  </span>}

                  {user.department && <span style={{
                    background: "#e8f5e9",
                    borderRadius: 6,
                    padding: "3px 10px",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}>
                    <span style={{ fontSize: "12px" }}>🏢</span> {user.department}
                  </span>}

                  {user.business_function && <span style={{
                    background: "#fff8e1",
                    borderRadius: 6,
                    padding: "3px 10px",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}>
                    <span style={{ fontSize: "12px" }}>💼</span> {user.business_function}
                  </span>}
                </div>

                </div>

                {hasOpProfile && (
                  <>
                    <div style={{
                      marginBottom: wideLayout ? 0 : 8,
                      height: wideLayout ? '100%' : undefined,
                      boxSizing: 'border-box',
                      border: "1px solid #edf1f4",
                      borderRadius: 10,
                      padding: "12px 14px",
                      background: "#fcfdfd",
                    }}>
                      <div style={{ fontWeight: 700, color: "#0b756c", fontSize: 13, marginBottom: 10 }}>
                        Cost and Billing Profile
                      </div>

                      {costCenterName && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 0.3 }}>GITHUB COST CENTER</div>
                          <div style={{ fontSize: 14, color: "#1f2328", fontWeight: 600, lineHeight: 1.35, wordBreak: "break-word" }}>
                            {costCenterName}
                          </div>
                        </div>
                      )}

                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "8px 18px",
                      }}>
                        {opData.crcc_business_name && (
                          <div style={{ gridColumn: "1 / -1" }}>
                            <div style={{ fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 0.3 }}>BUSINESS NAME</div>
                            <div style={{ fontSize: 13, wordBreak: "break-word", whiteSpace: "pre-line", color: "#1f2328" }}>
                              {opData.crcc_business_name}
                            </div>
                          </div>
                        )}
                        {opData.crcc_business_area && (
                          <div>
                            <div style={{ fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 0.3 }}>BUSINESS AREA</div>
                            <div style={{ fontSize: 13, wordBreak: "break-word", whiteSpace: "pre-line", color: "#1f2328" }}>
                              {opData.crcc_business_area}
                            </div>
                          </div>
                        )}
                        {opData.crcc_business_line && (
                          <div>
                            <div style={{ fontWeight: 600, color: "#777", fontSize: 11, letterSpacing: 0.3 }}>BUSINESS LINE</div>
                            <div style={{ fontSize: 13, wordBreak: "break-word", whiteSpace: "pre-line", color: "#1f2328" }}>
                              {opData.crcc_business_line}
                            </div>
                          </div>
                        )}
                      </div>

                      {(opData.rc_leader_name || opData.gps_indicator) && (
                        <div style={{
                          marginTop: 10,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                        }}>
                          {opData.rc_leader_name && (
                            <span style={{
                              background: "#eef6ff",
                              color: "#0969da",
                              border: "1px solid #c6ddff",
                              borderRadius: 14,
                              padding: "4px 10px",
                              fontSize: 12,
                            }}>
                              RC Leader: {opData.rc_leader_name}
                            </span>
                          )}
                          {opData.gps_indicator && (
                            <span style={{
                              background: "#ecfdf3",
                              color: "#116329",
                              border: "1px solid #b4e2c8",
                              borderRadius: 14,
                              padding: "4px 10px",
                              fontSize: 12,
                              fontWeight: 600,
                            }}>
                              GPS: {opData.gps_indicator}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}

                {profileExtra}
              </div>

              {/* RIGHT COLUMN: Organizations Table */}
              <div style={{
                flex: 1,
                minWidth: 0,
                background: '#fff',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(11,117,108,0.06)',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 18, color: "#0b756c", marginBottom: 8 }}>
                    Organization Memberships
                  </div>
                  {orgs.length > 0 && (
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <div style={{
                        fontSize: "12px",
                        padding: "3px 10px",
                        borderRadius: "20px",
                        background: "#f0f7fa",
                        fontWeight: 600,
                        color: "#444",
                      }}>
                        Total: {orgs.length}
                      </div>
                      {Object.entries(orgStatusCounts).map(([status, count]) => (
                        <div key={status} style={{
                          fontSize: "12px",
                          padding: "3px 10px",
                          borderRadius: "20px",
                          background: status === "Joined" ? "#e6f4ea" :
                            status === "Requested" ? "#fff8e1" :
                              status === "Removed" ? "#ffeaea" : "#f0f0f0",
                          color: status === "Joined" ? "#22A06B" :
                            status === "Requested" ? "#b38600" :
                              status === "Removed" ? "#d32f2f" : "#6f6c6c",
                          fontWeight: 500,
                        }}>
                          {status}: {count}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Table with scrolling */}
                <div style={{
                  overflowX: "auto",
                  maxHeight: "450px",
                  overflowY: "auto",
                  borderRadius: "8px",
                  border: "1px solid #f0f0f0",
                  flex: 1,
                }}>
                  <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "#fff",
                    tableLayout: "fixed"
                  }}>
                    <thead style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      background: "#f8fafc"
                    }}>
                      <tr>
                        <th style={{
                          textAlign: "left",
                          padding: "8px 12px",
                          fontWeight: 600,
                          fontSize: 13,
                          lineHeight: 1.1,
                          whiteSpace: "nowrap",
                          color: "#0b756c",
                          borderBottom: "2px solid #e0e7ef",
                          width: showCopilotColumn ? "24%" : "30%"
                        }}>Organization</th>
                        {showCopilotColumn && (
                          <th style={{
                            textAlign: "center",
                            padding: "8px 12px",
                            fontWeight: 600,
                            fontSize: 13,
                            lineHeight: 1.1,
                            whiteSpace: "nowrap",
                            color: "#0b756c",
                            borderBottom: "2px solid #e0e7ef",
                            width: "9%"
                          }}>Copilot</th>
                        )}
                        <th style={{
                          textAlign: "center",
                          padding: "8px 12px",
                          fontWeight: 600,
                          fontSize: 13,
                          lineHeight: 1.1,
                          whiteSpace: "nowrap",
                          color: "#0b756c",
                          borderBottom: "2px solid #e0e7ef",
                          width: showCopilotColumn ? "17%" : "20%"
                        }}>Status</th>
                        <th style={{
                          textAlign: "center",
                          padding: "8px 12px",
                          fontWeight: 600,
                          fontSize: 13,
                          lineHeight: 1.1,
                          whiteSpace: "nowrap",
                          color: "#0b756c",
                          borderBottom: "2px solid #e0e7ef",
                          width: "15%"
                        }}>Last Updated</th>
                        <th style={{
                          textAlign: "center",
                          padding: "8px 12px",
                          fontWeight: 600,
                          fontSize: 13,
                          lineHeight: 1.1,
                          whiteSpace: "nowrap",
                          color: "#0b756c",
                          borderBottom: "2px solid #e0e7ef",
                          width: showCopilotColumn ? "18%" : "20%"
                        }}>History</th>
                        <th style={{
                          textAlign: "center",
                          padding: "8px 12px",
                          fontWeight: 600,
                          fontSize: 13,
                          lineHeight: 1.1,
                          whiteSpace: "nowrap",
                          color: "#0b756c",
                          borderBottom: "2px solid #e0e7ef",
                          width: showCopilotColumn ? "17%" : "15%"
                        }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgs.length > 0 ? orgs.map((org, index) => (
                        <tr
                          key={org.id}
                          style={{
                            borderBottom: "1px solid #f0f0f0",
                            background: "#fff",
                            borderLeft: `3px solid ${org.membership_status === "Joined" ? "#22A06B" :
                              org.membership_status === "Requested" ? "#f59e0b" :
                                org.membership_status === "Removed" || org.membership_status === "Rejected" ? "#ef4444" :
                                  "#d0d7de"
                              }`,
                          }}
                        >
                          <td style={{ padding: "10px 16px", fontWeight: 500, color: "#000000ff", overflowWrap: "anywhere" }}>
                            {orgIdToName[org.id] || org.id}
                          </td>
                          {showCopilotColumn && (
                            <td style={{ padding: "10px 16px", textAlign: "center" }}>
                              {copilotOrgIds.includes(org.id) ? (
                                <Tooltip title="Has a Copilot license from this organization">
                                  <span data-testid="copilot-license-icon" style={{ display: "inline-flex" }}>
                                    <CopilotIcon aria-label="Copilot license" sx={{ fontSize: 18, color: "#1a1a1a" }} />
                                  </span>
                                </Tooltip>
                              ) : (
                                <span style={{ color: "#c0c7cf" }}>—</span>
                              )}
                            </td>
                          )}
                          <td style={{ padding: "10px 16px", textAlign: "center" }}>
                            <StatusBadge status={org.membership_status} />
                          </td>
                          <td style={{ padding: "10px 16px", textAlign: "center", fontSize: 13 }}>
                            {org.status_history && org.status_history.length > 0
                              ? new Date(org.status_history[org.status_history.length - 1].changed_date).toLocaleDateString()
                              : "-"}
                          </td>
                          <td style={{ padding: "10px 16px", textAlign: "center", whiteSpace: "nowrap" }}>
                            {/* <ExpandHistory history={org.status_history} /> */}
                            <HistoryModal history={org.status_history} />
                          </td>
                          <td style={{ padding: "10px 16px", textAlign: "center" }}>

                            {org.membership_status === "Joined" ? !canUpdateUserMembershipForOrg(org.id) ? (
                              <Tooltip title="You don't have permission to remove this user from the organization">
                                <span>
                                  <button
                                    className="revoke-access-button joined"
                                    disabled
                                    style={{ height: 32, minWidth: 90, fontSize: '10px', padding: '4px 8px', lineHeight: 1.2 }}
                                  >
                                    Revoke Access
                                  </button>
                                </span>
                              </Tooltip>
                            ) : (
                              <ReusableActions
                                buttonStyle={{ height: 32, minWidth: 90, fontSize: '10px', padding: '4px 8px', lineHeight: 1.2 }}
                                params={{
                                  row: {
                                    ...org,
                                    status: "joined",
                                    name: user.full_name || `${user.last_name}, ${user.first_name}`,
                                    email: user.email,
                                    orgName: orgIdToName[org.id] || org.id,
                                  },
                                }}
                                action={async (rejectReason, otherReason) => {
                                  setActionLoading(true);
                                  const result = await updateUserMembershipBulk({
                                    body: {
                                      platforms: {
                                        [platformData?._id]: {
                                          organizations: {
                                            [org.id]: {
                                              membership_status: "Remove-Requested",
                                              reason: rejectReason === "Other" ? otherReason : rejectReason,
                                              changed_by: loggedInEmail,
                                            },
                                          },
                                        },
                                      },
                                      user_ids: [user._id],
                                    },
                                    invalidateStatuses: ["Joined", "Remove-Requested"],
                                  });
                                  if (!checkOrgAdminError(result, (orgIdToName[org.id].toUpperCase()))) {
                                    await updateSelectedUser();
                                    refreshData();
                                  }
                                  if (isMounted.current) setActionLoading(false);
                                }}
                              />
                            ) : org.membership_status === "Removed" || org.membership_status === "Rejected" || org.membership_status === "Remove-Requested" ? (
                              <ResetProfileButton
                                disabledTooltip={!canUpdateUserMembershipForOrg(org.id) ? "You don't have permission to reset this user's profile" : undefined}
                                onReset={async () => {
                                  setActionLoading(true);
                                  const result = await updateUserMembershipBulk({
                                    body: {
                                      platforms: {
                                        [platformData?._id]: {
                                          organizations: {
                                            [org.id]: {
                                              membership_status: "Not-Requested",
                                              reason: "Reset by admin",
                                              changed_by: loggedInEmail,
                                            },
                                          },
                                        },
                                      },
                                      user_ids: [user._id],
                                    },
                                    invalidateStatuses: ["Removed", "Remove-Requested"],
                                  });
                                  if (!checkOrgAdminError(result, (orgIdToName[org.id].toUpperCase()))) {
                                    await updateSelectedUser();
                                    refreshData();
                                  }
                                  if (isMounted.current) setActionLoading(false);
                                }}
                                email={user.email}
                                loading={actionLoading || isUpdating}
                              />
                            ) : org.membership_status === "Not-Requested" ? (
                              <Tooltip
                                title={
                                  !canUpdateUserMembershipForOrg(org.id)
                                    ? "You don't have permission to add this user to the organization"
                                    : "Add user to organization"
                                }
                              >
                                <span>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="primary"
                                  disabled={isUpdating || actionLoading || !canUpdateUserMembershipForOrg(org.id)}
                                  onClick={async () => {
                                    setActionLoading(true);
                                    const result = await updateUserMembershipBulk({
                                      body: {
                                        platforms: {
                                          [platformData?._id]: {
                                            organizations: {
                                              [org.id]: {
                                                membership_status: "Requested",
                                                changed_by: loggedInEmail,
                                                reason: `Adding from Admin Portal by ${loggedInEmail}`,
                                              },
                                            },
                                          },
                                        },
                                        user_ids: [user._id],
                                      },
                                    });
                                    if (!checkOrgAdminError(result, (orgIdToName[org.id].toUpperCase()))) {
                                      await updateSelectedUser();
                                      refreshData();
                                    }

                                    if (isMounted.current) setActionLoading(false);
                                  }}
                                  sx={{
                                    fontSize: '10px',
                                    textTransform: 'none',
                                    borderColor: '#0b756c',
                                    color: '#0b756c',
                                    minWidth: 90,
                                    padding: '2px 10px 2px 6px',
                                    height: 32,
                                    lineHeight: 1.2,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    '& .MuiButton-startIcon': {
                                      minWidth: 0,
                                      marginRight: 6,
                                    },
                                    '&:hover': {
                                      borderColor: '#0b756c',
                                      backgroundColor: 'rgba(11, 117, 108, 0.08)',
                                    },
                                  }}
                                > <Add sx={{ fontSize: 14, minWidth: 0, marginRight: 0.5 }} />
                                  {(isUpdating || actionLoading) ? 'ADDING...' : 'ADD TO ORG'}
                                </Button>
                                </span>
                              </Tooltip>
                            ) : (
                              <span style={{ color: "#999" }}>—</span>
                            )}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan={showCopilotColumn ? 6 : 5} style={{
                            color: "#d32f2f",
                            fontWeight: 500,
                            padding: "20px",
                            textAlign: "center"
                          }}>
                            No organizations found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <AdminReactivateUserModal
        show={showReactivateConfirm}
        close={() => setShowReactivateConfirm(false)}
        email={reactivateTarget}
        reactivateUser={reactivateUser}
        businessId={platformData?.ref_id}
        user={user}
        triggerActiveUserStatus={triggerActiveUserStatus}
        isLoading={isReactivating}
      />
    </>
  );
};

export default UserOrgSearch;